import { Box, Link as MuiLink, Skeleton, Typography, useTheme } from '@mui/material';
import { Fragment, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { fontSize, lineHeight } from '@verantos-dev/shared-ui-components/tokens';

import { PageHeaderBreadcrumb } from '../PageHeader/types';

export type SubPageBreadcrumbHeaderProps = {
  /** Stable id prefix for testids. */
  id: string;
  /**
   * Breadcrumb trail rendered on top of the title. The leading crumb is
   * always the project; the final crumb is the current page (rendered as
   * plain, non-link text). Each crumb may carry an `href` (RouterLink) or
   * an `onClick` (e.g. exit-guard interception) — both keep their
   * back-link behavior.
   */
  breadcrumbs: PageHeaderBreadcrumb[];
  /**
   * Big page title rendered immediately below the breadcrumb row. Omit it
   * (and `titleSlot`) for **breadcrumb-only** mode — used on pages whose body
   * (e.g. an artifact card) already renders its own title, so the breadcrumb
   * sits above that existing title rather than introducing a second one.
   */
  title?: string;
  /** When true, the title renders as a skeleton (name still loading). */
  isTitleLoading?: boolean;
  /**
   * Optional slot rendered in place of the plain title — lets callers
   * wrap the title in inline-edit affordances, status badges, etc. When
   * provided, `title` is ignored for rendering (still used for a11y).
   */
  titleSlot?: ReactNode;
  /** Optional content rendered to the right of the title row (actions). */
  rightContent?: ReactNode;
};

/**
 * Condensed page header for project sub-pages: a slim breadcrumb trail on
 * top with the page title directly underneath (both kept, per OR-6123 F8).
 *
 * This replaces the project-page header breadcrumb that F3 removes — each
 * artifact detail page now renders its own breadcrumb + title so deep
 * views stay grounded. Modeled on the legacy `CohortBuilderPageHeader`
 * but trimmed: no info bar, no tabs, smaller vertical rhythm.
 *
 * The component is flag-agnostic — callers gate it on
 * `research_assistant_gen_3` where a legacy route can still reach the page.
 */
export const SubPageBreadcrumbHeader = ({
  id,
  breadcrumbs,
  title,
  isTitleLoading = false,
  titleSlot,
  rightContent,
}: SubPageBreadcrumbHeaderProps) => {
  const theme = useTheme();

  return (
    <Box
      id={id}
      data-testid={id}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        // 24px gen-3 project gutter (OR-6123 F9) so the breadcrumb + title
        // line up with the page body below (AV card / cohort header / CG meta).
        paddingX: 6,
        paddingTop: 2,
        paddingBottom: 1.5,
      }}
    >
      <Box
        data-testid={`${id}-breadcrumbs`}
        sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}
      >
        {breadcrumbs.map(({ label, href, onClick, isLoading }, index) => {
          const isLast = breadcrumbs.length !== 1 && index === breadcrumbs.length - 1;
          const separator =
            index === 0 ? null : (
              <Typography
                component="span"
                variant="caption"
                aria-hidden
                sx={{ color: theme.palette.text.disabled, userSelect: 'none', mx: 0.75 }}
              >
                /
              </Typography>
            );

          // Shared click core. The two crumb variants below render different
          // host elements (anchor via RouterLink, button via component="button"),
          // so each wraps this with its own correctly-typed handler.
          const handleClick = (event: React.MouseEvent<HTMLElement>) => {
            // Cmd/Ctrl/Shift+click → let the browser open a new tab via href.
            if (event.ctrlKey || event.metaKey || event.shiftKey) {
              if (href) return;
              event.preventDefault();
              return;
            }
            if (onClick) {
              event.preventDefault();
              onClick(
                event as React.MouseEvent<HTMLAnchorElement> & React.MouseEvent<HTMLSpanElement>
              );
            }
          };

          const crumbSx = {
            color: isLast ? theme.palette.text.primary : theme.palette.text.secondary,
            textDecoration: 'none',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: `${fontSize[16]}px`,
            lineHeight: `${lineHeight.normal}%`,
          } as const;

          let crumb: ReactNode;
          if (isLoading) {
            crumb = (
              <Skeleton
                variant="text"
                width={100}
                height={20}
                data-testid={`${id}-breadcrumb-skeleton-${index + 1}`}
              />
            );
          } else if ((href || onClick) && !isLast) {
            crumb = href ? (
              <MuiLink
                component={RouterLink}
                to={href}
                underline="hover"
                variant="caption"
                onClick={(event: React.MouseEvent<HTMLAnchorElement>) => handleClick(event)}
                data-testid={`${id}-breadcrumb-link-${index + 1}`}
                sx={crumbSx}
              >
                {label}
              </MuiLink>
            ) : (
              <MuiLink
                underline="hover"
                variant="caption"
                component="button"
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => handleClick(event)}
                data-testid={`${id}-breadcrumb-link-${index + 1}`}
                sx={{ ...crumbSx, cursor: 'pointer' }}
              >
                {label}
              </MuiLink>
            );
          } else {
            crumb = (
              <Typography
                component="span"
                variant="caption"
                noWrap
                data-testid={`${id}-breadcrumb-current-${index + 1}`}
                sx={crumbSx}
              >
                {label}
              </Typography>
            );
          }

          return (
            <Fragment key={`${label}-${index}`}>
              {separator}
              {crumb}
            </Fragment>
          );
        })}
      </Box>
      {/* Title row — omitted entirely in breadcrumb-only mode (no title /
          titleSlot / rightContent) so the breadcrumb can sit above a body
          that already owns its title. */}
      {titleSlot || title !== undefined || isTitleLoading || rightContent ? (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            rowGap: 1,
          }}
        >
          {titleSlot ??
            (isTitleLoading ? (
              <Skeleton
                variant="text"
                width={240}
                height={36}
                data-testid={`${id}-title-loading`}
              />
            ) : title !== undefined ? (
              <Typography
                variant="h5"
                component="h1"
                data-testid={`${id}-title`}
                sx={{ fontWeight: 400, minWidth: 0, overflowWrap: 'anywhere' }}
              >
                {title}
              </Typography>
            ) : (
              <span />
            ))}
          {rightContent ? (
            <Box
              data-testid={`${id}-actions`}
              sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}
            >
              {rightContent}
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};
